import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AdminListPage } from '@/features/admin/components/AdminListPage'
import { typedCollection, getCollection } from '@/lib/firebase/firestore'
import type { BoardTemplateDocument, ServiceDocument } from '@/types'
import { useBoardTemplates } from '../hooks/useBoardTemplates'
import { boardTemplatesService } from '../services/boardTemplates.service'

type Template = BoardTemplateDocument & { id: string }
type Service = ServiceDocument & { id: string }

interface FormState {
  name: string
  description: string
  defaultForServiceIds: string[]
}

const EMPTY_FORM: FormState = { name: '', description: '', defaultForServiceIds: [] }

export function AdminBoardTemplatesPage() {
  const { user } = useAuth()
  const { templates, loading, error, reload } = useBoardTemplates()
  const [services, setServices] = useState<Service[]>([])
  const [servicesError, setServicesError] = useState<string | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Template | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const result = await getCollection(typedCollection<ServiceDocument>('services'))
        if (!cancelled) setServices(result)
      } catch (e) {
        if (!cancelled) setServicesError(formatUserFacingError(e, 'We could not complete that action. Please try again.'))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const servicesById = useMemo(() => {
    const m = new Map<string, Service>()
    for (const s of services) m.set(s.id, s)
    return m
  }, [services])

  function openCreate() {
    setEditTarget(null)
    setForm(EMPTY_FORM)
    setSubmitError(null)
    setDialogOpen(true)
  }

  function openEdit(t: Template) {
    setEditTarget(t)
    setForm({
      name: t.name,
      description: t.description ?? '',
      defaultForServiceIds: [...(t.defaultForServiceIds ?? [])],
    })
    setSubmitError(null)
    setDialogOpen(true)
  }

  function toggleService(id: string) {
    setForm((f) =>
      f.defaultForServiceIds.includes(id)
        ? { ...f, defaultForServiceIds: f.defaultForServiceIds.filter((x) => x !== id) }
        : { ...f, defaultForServiceIds: [...f.defaultForServiceIds, id] },
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) {
      setSubmitError(withErrorCode('Name is required.', 'app/validation'))
      return
    }
    if (!user) {
      setSubmitError(withErrorCode('Not signed in.', 'app/validation'))
      return
    }
    setSubmitting(true)
    setSubmitError(null)
    try {
      const description = form.description.trim()
      if (editTarget) {
        await boardTemplatesService.update(editTarget.id, {
          name: form.name.trim(),
          description: description || undefined,
          defaultForServiceIds: form.defaultForServiceIds,
        })
      } else {
        await boardTemplatesService.createBlank({
          name: form.name.trim(),
          ...(description ? { description } : {}),
          defaultForServiceIds: form.defaultForServiceIds,
          createdBy: user.uid,
        })
      }
      setDialogOpen(false)
      await reload()
    } catch (err) {
      setSubmitError(formatUserFacingError(err, 'We could not complete that action. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleArchive(t: Template) {
    if (!window.confirm('Archive this template? Existing boards using it are unaffected.')) return
    try {
      await boardTemplatesService.archive(t.id)
      await reload()
    } catch (err) {
      alert(formatUserFacingError(err, 'We could not complete that action. Please try again.'))
    }
  }

  return (
    <AdminListPage
      title="Board Templates"
      action={
        <Button size="sm" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          New Template
        </Button>
      }
      loading={loading}
      error={error?.message ?? servicesError}
      skeletonRows={5}
      dialog={
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editTarget ? 'Edit Template' : 'New Template'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tpl-name">Name</Label>
                <Input
                  id="tpl-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tpl-desc">Description</Label>
                <Textarea
                  id="tpl-desc"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Default for services</Label>
                <div className="max-h-56 overflow-y-auto rounded-md border p-2 space-y-1">
                  {services.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-2">No services found.</p>
                  ) : (
                    services.map((s) => {
                      const checked = form.defaultForServiceIds.includes(s.id)
                      return (
                        <label
                          key={s.id}
                          className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted cursor-pointer text-sm"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleService(s.id)}
                          />
                          <span>{s.title}</span>
                        </label>
                      )
                    })
                  )}
                </div>
              </div>
              {submitError && <p className="text-sm text-destructive">{submitError}</p>}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Saving…' : editTarget ? 'Save' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Default for</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                No templates yet.
              </TableCell>
            </TableRow>
          ) : (
            templates.map((t) => {
              const count = t.defaultForServiceIds?.length ?? 0
              const names = (t.defaultForServiceIds ?? [])
                .map((id) => servicesById.get(id)?.title)
                .filter((x): x is string => !!x)
              return (
                <TableRow
                  key={t.id}
                  className="cursor-pointer"
                  onClick={() => openEdit(t)}
                >
                  <TableCell className="font-medium text-sm">{t.name}</TableCell>
                  <TableCell className="max-w-[320px]">
                    <span className="truncate block text-sm text-muted-foreground">
                      {t.description ?? '—'}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <span title={names.join(', ')}>
                      {count} {count === 1 ? 'service' : 'services'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={(e) => {
                          e.stopPropagation()
                          openEdit(t)
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) => {
                          e.stopPropagation()
                          void handleArchive(t)
                        }}
                      >
                        Archive
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </AdminListPage>
  )
}
